import React, { useContext, useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { db } from "../../utils/firebase/firebaseConfig";
import SidebarWrapper from "../../components/wrapper/SidebarWrapper";
import { DocNursePage } from "../../utils/others/AccessController";
import { AuthContext } from "../../contexts/Auth.context";
import { ActiveSidebarContext } from "../../contexts/ActiveSidebarContext";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { customColors } from "../../assets/styles/Theme";
import CustomButton from "../../components/others/CustomButton";

const InventoryManagement = () => {
  const { userData } = useContext(AuthContext);
  const { setShowBottomNavigator, setActiveSidebarText, setTopLabel } =
    useContext(ActiveSidebarContext);

  const [inventory, setInventory] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    amount: "",
    expiryDate: "",
    receiveDate: "",
    consumeDate: "",
  });
  const [selectedId, setSelectedId] = useState(null);
  const [addUpdateLoading, setAddUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    setShowBottomNavigator(false);
    setTopLabel("Inventory Management");
    setActiveSidebarText("profile");

    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInventory = async () => {
    try {
      const q = query(
        collection(db, "inventories"),
        where("userId", "==", userData.uid)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const inventoryDoc = querySnapshot.docs[0]; // Get the first inventory document
        const inventoryData = inventoryDoc.data();

        // Ensure inventories array exists and assign id from productId
        const formattedInventories = (inventoryData.inventories || []).map(
          (product) => ({
            ...product,
            id: product.productId, // Ensure DataGrid gets a unique id
          })
        );

        setInventory({
          id: inventoryDoc.id, // Inventory document ID
          inventories: formattedInventories, // Products with id
        });
        const price = formattedInventories.reduce(
          (total, item) => parseFloat(item.price * item.amount) + total,
          0
        );
        setTotalPrice(price?.toFixed(2));
      } else {
        // If no inventory exists, set an empty array
        setInventory({ id: null, inventories: [] });
      }
    } catch (error) {
      toast.error("Error fetching inventory: " + error.message);
    }
  };

  const handleAddOrUpdate = async () => {
    try {
      setAddUpdateLoading(true);

      // Get the user's inventory document
      const inventoryRef = collection(db, "inventories");
      const q = query(inventoryRef, where("userId", "==", userData?.uid));
      const querySnapshot = await getDocs(q);

      let inventoryDoc;
      if (!querySnapshot.empty) {
        inventoryDoc = querySnapshot.docs[0]; // Get the existing inventory document
      }

      if (inventoryDoc) {
        // If inventory exists, update it
        const inventoryData = inventoryDoc.data();
        let updatedInventories = [...inventoryData.inventories];

        if (selectedId) {
          // Update existing product
          updatedInventories = updatedInventories.map((item) =>
            item.productId === selectedId ? { ...item, ...formData } : item
          );
        } else {
          // Add new product with auto-generated productId
          const productId = uuidv4();
          updatedInventories.push({ ...formData, productId });
        }

        await updateDoc(doc(db, "inventories", inventoryDoc.id), {
          inventories: updatedInventories,
        });
      } else {
        // If inventory does not exist, create a new one
        const productId = uuidv4();
        await addDoc(collection(db, "inventories"), {
          userId: userData?.uid,
          inventories: [{ ...formData, productId }],
        });
      }

      setOpen(false);
      setFormData({
        name: "",
        price: "",
        amount: "",
        expiryDate: "",
        receiveDate: "",
        consumeDate: "",
      });

      fetchInventory();
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setAddUpdateLoading(false);
    }
  };
  const handleDelete = async (productId) => {
    try {
      setDeleteLoading(true);
      const q = query(
        collection(db, "inventories"),
        where("userId", "==", userData.uid)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const inventoryDoc = querySnapshot.docs[0]; // Get the inventory document
        const inventoryRef = doc(db, "inventories", inventoryDoc.id);
        const inventoryData = inventoryDoc.data();

        // Filter out the product that needs to be deleted
        const updatedInventories = inventoryData.inventories.filter(
          (product) => product.productId !== productId
        );

        // Update the document with the new inventories array
        await updateDoc(inventoryRef, { inventories: updatedInventories });
        toast.error("Product deleted successfully");

        fetchInventory(); // Refresh the inventory list
      }
    } catch (error) {
      toast.error("Error deleting product: " + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const capitalizeWords = (str) => {
    return str
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before capital letters (camelCase support)
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };

  const columns = [
    { field: "name", headerName: "Name", width: 150 },
    { field: "price", headerName: "Price", width: 100 },
    { field: "amount", headerName: "Amount", width: 100 },
    { field: "expiryDate", headerName: "Expiry Date", width: 150 },
    { field: "receiveDate", headerName: "Receive Date", width: 150 },
    { field: "consumeDate", headerName: "Consume Date", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <>
          <Button
            onClick={() => {
              setSelectedId(params.row.id);
              setOpen(true);
              setFormData(params.row);
            }}
          >
            Edit
          </Button>
          <LoadingButton
            onClick={() => handleDelete(params.row.id)}
            loading={deleteLoading}
            variant="outlined"
            color="error"
          >
            Delete
          </LoadingButton>
        </>
      ),
    },
  ];

  return (
    <DocNursePage>
      <SidebarWrapper>
        <CustomButton
          label="Add Product"
          onClick={() => {
            setSelectedId(null);
            setOpen(true);
          }}
        />

        <Typography variant="h6" sx={{ m: 1 }}>
          Total: ${totalPrice}
        </Typography>

        <Modal
          open={open}
          onClose={() => setOpen(false)}
          sx={{ overflow: "auto" }}
        >
          <Paper
            sx={{
              width: { md: "50%", xs: "90%" },
              px: 2,
              py: 3,
              outline: "none",
              margin: "auto",
              marginTop: { md: "10%", xs: "2vh" },
              borderRadius: "0.5rem",
            }}
          >
            <Typography variant="h6">
              {selectedId ? "Edit Product" : "Add Product"}
            </Typography>
            <Box>
              {Object.keys(formData).map((key) => (
                <Box key={key}>
                  <label>{capitalizeWords(key)}</label>
                  <TextField
                    key={key}
                    // label={capitalizeWords(key)} // Properly formatted label
                    fullWidth
                    required
                    margin="dense"
                    value={formData[key]}
                    onChange={(e) =>
                      setFormData({ ...formData, [key]: e.target.value })
                    }
                    type={
                      key?.toLowerCase()?.includes("date")
                        ? "date"
                        : key === "price"
                        ? "number"
                        : "text"
                    }
                    disabled={key?.toLowerCase()?.includes("id")}
                  />
                </Box>
              ))}
            </Box>
            <Box sx={{ textAlign: "end", mt: 2 }}>
              <LoadingButton
                sx={{ backgroundColor: customColors.color_red }}
                onClick={() => setOpen(false)}
                variant="contained"
              >
                Close
              </LoadingButton>
              <LoadingButton
                sx={{ backgroundColor: customColors.color_primary, ml: 3 }}
                onClick={handleAddOrUpdate}
                variant="contained"
                loading={addUpdateLoading}
              >
                {selectedId ? "Update" : "Add"}
              </LoadingButton>
            </Box>
          </Paper>
        </Modal>

        <Typography variant="h5" sx={{ m: 1 }}>
          Products
        </Typography>
        <DataGrid
          rows={inventory?.inventories || []}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
        />
      </SidebarWrapper>
    </DocNursePage>
  );
};

export default InventoryManagement;
